import * as yup from 'yup';
import { trimString } from '../../../../utils/trimString';
import { getNominationRoundYear } from '../../../../utils/getNominationRoundYear';
import { ELECTION_TYPE, NOMINATION_STATUS } from '../../../../utils/constants';
import { devError } from '../../../../utils/devLog';

// Wraps a yup test() body so that a thrown error does not surface as a
// misleading validation message to the user. A crashing validator is treated
// as a pass — the form can still be submitted and the backend has its own
// duplicate/expired/elected checks as the real safety net.
const safeValidation = (validationName, fn) => (value, context) => {
  try {
    return fn(value, context);
  } catch (err) {
    devError(`Validation "${validationName}" threw:`, err);
    return true;
  }
};

// Constants
export const currentDate = new Date();
export const phdAwardedYearMaxValue = currentDate.getFullYear();
export const pdfFileInputMessage = 'PDF files of up to 2mb are permitted';
export const pdfFileSize = 2000000;
export const minimumYear = 1960;
export const descriptorErrorMessage =
  'Please choose at least one discipline descriptor';
export const interdisciplinaryErrorMessage =
  'Please choose at least two sectional committees';
export const defaultMessage = 'Required Field';
export const notAPdfFile = 'Not a pdf. Please upload a valid file.';

// Configure yup locale
yup.setLocale({
  mixed: {
    required: defaultMessage,
  },
  string: {
    min: ({ min }) => `Must be at least ${min} characters`,
    email: 'Invalid email',
  },
  number: {
    integer: 'Must be an integer',
  },
});

// File validators
export const validatePdfType = (file) => {
  if (
    !file ||
    typeof file !== 'object' ||
    (!Array.isArray(file) && !file.length)
  ) {
    return false;
  }

  if (file.length === 0) {
    return false;
  }

  const selectedFile = file[0];

  if (!selectedFile) {
    return false;
  }

  const isPdf = selectedFile.type === 'application/pdf';

  return isPdf;
};

export const validatePdfSize = (file) => {
  if (
    !file ||
    typeof file !== 'object' ||
    (!Array.isArray(file) && !file.length)
  ) {
    return false;
  }

  if (file.length === 0) {
    return false;
  }

  if (!file[0]) {
    return false;
  }

  const isValidSize = file[0]?.size <= pdfFileSize && file[0]?.size > 0;

  return isValidSize;
};

export const validateFileRequired = (file) => {
  const hasValidFile =
    file &&
    typeof file === 'object' &&
    (Array.isArray(file) || file.length !== undefined) &&
    file.length > 0;

  return hasValidFile;
};

// Candidate validation factory
export const createCandidateValidation = (
  validationName,
  errorMessage,
  checkFunction,
) =>
  safeValidation(validationName, (value, context) => {
    if (!value) return true;

    const { candidateArray, nominationArray, nominationStateArray } =
      context.options;
    const { name, lastName, email } = context.parent;

    const matchingCandidates = candidateArray?.filter((candidate) => {
      const nameMatch =
        candidate.attributes.name?.toLowerCase() === name?.toLowerCase() &&
        candidate.attributes.lastName?.toLowerCase() ===
          lastName?.toLowerCase();

      const emailMatch =
        email &&
        candidate.attributes.email &&
        candidate.attributes.email.toLowerCase() === email.toLowerCase();

      return nameMatch || emailMatch;
    });

    if (!matchingCandidates?.length) return true;

    const hasMatch = checkFunction({
      matchingCandidates,
      candidateArray,
      nominationArray,
      nominationStateArray,
    });

    if (hasMatch) {
      const error = new yup.ValidationError(errorMessage, value, context.path);
      error.type = validationName;
      return error;
    }

    return true;
  });

export const validateExpiredNomination = createCandidateValidation(
  'expired-nomination',
  'Candidate has a recently expired nomination.',
  ({ matchingCandidates, nominationArray, nominationStateArray }) => {
    const nominationRoundYear = getNominationRoundYear(currentDate);

    const expiredNominations = nominationArray?.filter((nomination) => {
      const state = nominationStateArray?.find(
        (nominationState) =>
          nominationState.attributes.nominationId === nomination.id,
      );

      if (!state?.attributes?.lastConsideredDate) return false;

      const lastConsideredYear = getNominationRoundYear(
        new Date(state.attributes.lastConsideredDate),
      );

      return (
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.EXPIRED &&
        lastConsideredYear >= nominationRoundYear - 2 &&
        lastConsideredYear < nominationRoundYear
      );
    });

    if (!expiredNominations.length) return false;

    return matchingCandidates.some((candidate) =>
      expiredNominations.some(
        (nomination) => nomination.attributes.candidateId === candidate.id,
      ),
    );
  },
);

export const validateAlreadyElected = createCandidateValidation(
  'already-elected',
  'Candidate has already been elected.',
  ({ matchingCandidates, nominationArray }) => {
    const electedNominations = nominationArray?.filter(
      (nomination) =>
        nomination.attributes.nominationStatusTypeId ===
        NOMINATION_STATUS.ELECTED,
    );

    if (!electedNominations.length) return false;

    return matchingCandidates.some((candidate) =>
      electedNominations.some(
        (nomination) => nomination.attributes.candidateId === candidate.id,
      ),
    );
  },
);

export const validateDeceasedCandidate = createCandidateValidation(
  'deceased-candidate',
  'Candidate is deceased and cannot be nominated.',
  ({ matchingCandidates }) =>
    matchingCandidates.some(
      (candidate) =>
        candidate.attributes.deceasedDate !== null &&
        candidate.attributes.deceasedDate !== undefined,
    ),
);

// Active nomination conflict detection
const findActiveNominationConflicts = (context) => {
  const {
    candidateArray,
    nominationArray,
    currentUserId,
    isAdmin,
    proposerSeconder,
  } = context.options;
  const { name, lastName, email } = context.parent;

  if (!candidateArray?.length || !nominationArray?.length) return null;
  if (isAdmin) return null;

  const nameMatchCandidates = candidateArray.filter(
    (candidate) =>
      candidate.attributes.name?.toLowerCase() === name?.toLowerCase() &&
      candidate.attributes.lastName?.toLowerCase() === lastName?.toLowerCase(),
  );

  const emailMatchCandidates = email
    ? candidateArray.filter(
        (candidate) =>
          candidate.attributes.email &&
          candidate.attributes.email.toLowerCase() === email.toLowerCase(),
      )
    : [];

  const conflictingNomination = nominationArray.find((nomination) => {
    const isDraft =
      nomination.attributes.nominationStatusTypeId === NOMINATION_STATUS.DRAFT;
    if (!isDraft) return false;

    const matchesCandidate =
      emailMatchCandidates.some(
        (c) => c.id === nomination.attributes.candidateId,
      ) ||
      nameMatchCandidates.some(
        (c) => c.id === nomination.attributes.candidateId,
      );
    if (!matchesCandidate) return false;

    const matchingProposerSeconder = proposerSeconder?.find(
      (ps) => ps.attributes.nominationId === nomination.id,
    );
    const conflictingProposerUserId =
      matchingProposerSeconder?.attributes?.proposerUserId;

    return (
      conflictingProposerUserId && conflictingProposerUserId !== currentUserId
    );
  });

  if (conflictingNomination) {
    const isEmailConflict = emailMatchCandidates.some(
      (c) => c.id === conflictingNomination.attributes.candidateId,
    );

    if (isEmailConflict) return 'email';
    return 'name';
  }

  return null;
};

export const validateActiveNominationDuplicate = safeValidation(
  'active-nomination-duplicate',
  (value, context) => {
    if (!value) return true;

    const matchType = findActiveNominationConflicts(context);
    if (matchType === 'email') {
      const error = new yup.ValidationError(
        'This candidate already has an active nomination by another proposer.',
        value,
        context.path,
      );
      error.type = 'active-nomination-duplicate';
      return error;
    }

    if (matchType === 'name') {
      const error = new yup.ValidationError(
        'A candidate with this name may already have an active nomination by another proposer. Please verify the candidate details.',
        value,
        context.path,
      );
      error.type = 'active-nomination-warning';
      return error;
    }

    return true;
  },
);

// Draft conflict detection
const findDraftConflicts = (context) => {
  const { draftNominationArray, currentUserId, isAdmin } = context.options;
  const { name, lastName, email } = context.parent;

  if (!draftNominationArray?.length) return null;
  if (isAdmin) return null;

  let hasNameConflict = false;
  let hasEmailConflict = false;

  for (const draft of draftNominationArray) {
    if (draft.userId !== currentUserId) {
      try {
        const draftData = JSON.parse(draft.attributes.value);

        const nameMatch =
          draftData.name?.trim().toLowerCase() === name?.trim().toLowerCase() &&
          draftData.lastName?.trim().toLowerCase() ===
            lastName?.trim().toLowerCase();

        const emailMatch =
          email &&
          draftData.email &&
          draftData.email.trim().toLowerCase() === email.trim().toLowerCase();

        if (emailMatch) {
          hasEmailConflict = true;
          break;
        }
        if (nameMatch) hasNameConflict = true;
      } catch (error) {
        // skip unparseable draft data
      }
    }
  }

  if (hasEmailConflict) return 'email';
  if (hasNameConflict) return 'name';
  return null;
};

export const validateDraftDuplicate = safeValidation(
  'draft-duplicate',
  (value, context) => {
    if (!value) return true;

    const matchType = findDraftConflicts(context);

    if (matchType === 'email') {
      const error = new yup.ValidationError(
        'Another proposer is already preparing a nomination for this candidate.',
        value,
        context.path,
      );
      error.type = 'draft-duplicate';
      return error;
    }

    if (matchType === 'name') {
      const error = new yup.ValidationError(
        'Another proposer may be preparing a nomination for a candidate with this name. Please verify the candidate details.',
        value,
        context.path,
      );
      error.type = 'draft-duplicate-warning';
      return error;
    }

    return true;
  },
);

// Conditional validators for referee fields
export const conditionalNameValidation = (fieldName, conditionField) =>
  yup.string().when(conditionField, {
    is: (item) => item && typeof item.value === 'number' && item.value === 0,
    then: yup
      .string()
      .transform(trimString)
      .min(2)
      .max(40)
      .matches(
        /^[a-zA-Z\s'-]+$/,
        `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`,
      )
      .required(),
    otherwise: yup.string().notRequired().nullable(),
  });

export const conditionalNameValidationWithElectionType = (
  fieldName,
  conditionField,
) =>
  yup.string().when(['electionTypeId', conditionField], {
    is: (electionTypeId, item) =>
      electionTypeId === ELECTION_TYPE.ORDINARY &&
      item &&
      typeof item.value === 'number' &&
      item.value === 0,
    then: yup
      .string()
      .transform(trimString)
      .min(2)
      .max(40)
      .matches(
        /^[a-zA-Z\s'-]+$/,
        `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`,
      )
      .required(),
    otherwise: yup.string().notRequired().nullable(),
  });
